import { useEffect, useState } from "react";

export type NavbarVisibility = {
  hideMain: boolean;
  hideHelp: boolean;
  hideUser: boolean;
};

export const readNavbarVisibility = (): NavbarVisibility => ({
  hideMain: document.body?.classList.contains("hide-main-navbar") ?? false,
  hideHelp: document.body?.classList.contains("hide-help-menu") ?? false,
  hideUser: document.body?.classList.contains("hide-user-menu") ?? false,
});

// history.pushState/replaceState are patched ONCE and shared across all navbar instances
// (ref-counted). Previously each instance patched and restored the global itself, so with
// two embedded navbars the first to unmount restored an earlier closure and silently
// disabled route-visibility updates for the remaining navbar.
const routeChangeListeners = new Set<() => void>();
let historyPatchCount = 0;
let originalPushState: History["pushState"] | null = null;
let originalReplaceState: History["replaceState"] | null = null;

// Deferred: Next.js calls history.replaceState inside a React insertion effect, where updates must not be scheduled.
// Through a promise where the browser predates queueMicrotask (the web component runs on partner pages).
const afterThisTask = (fn: () => void) =>
  typeof queueMicrotask === "function" ? queueMicrotask(fn) : void Promise.resolve().then(fn);
const notifyRouteChange = () => afterThisTask(() => routeChangeListeners.forEach((fn) => fn()));

const installHistoryPatch = () => {
  if (historyPatchCount++ > 0) return;
  const realPushState = window.history.pushState.bind(window.history);
  const realReplaceState = window.history.replaceState.bind(window.history);
  originalPushState = window.history.pushState;
  originalReplaceState = window.history.replaceState;
  window.history.pushState = function (...args: Parameters<History["pushState"]>) {
    realPushState(...args);
    notifyRouteChange();
  };
  window.history.replaceState = function (...args: Parameters<History["replaceState"]>) {
    realReplaceState(...args);
    notifyRouteChange();
  };
};

const uninstallHistoryPatch = () => {
  if (historyPatchCount === 0 || --historyPatchCount > 0) return;
  if (originalPushState) window.history.pushState = originalPushState;
  if (originalReplaceState) window.history.replaceState = originalReplaceState;
  originalPushState = null;
  originalReplaceState = null;
};

export const subscribeToRouteChanges = (listener: () => void) => {
  routeChangeListeners.add(listener);
  installHistoryPatch();
  window.addEventListener("popstate", listener);
  return () => {
    routeChangeListeners.delete(listener);
    uninstallHistoryPatch();
    window.removeEventListener("popstate", listener);
  };
};

export const useNavbarVisibility = () => {
  const [visibility, setVisibility] = useState(readNavbarVisibility);

  useEffect(() => {
    const update = () => setVisibility(readNavbarVisibility());
    const unsubscribe = subscribeToRouteChanges(update);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => {
      unsubscribe();
      observer.disconnect();
    };
  }, []);

  return visibility;
};
